/**
 * LevelBadge — o emblema hexagonal de nível.
 * Fonte: handoff/dc/Emblema.dc.html, reproduzido camada por camada.
 *
 * Três hexágonos empilhados (externo com o gradiente da faixa, um vazado com
 * a cor de fundo, e o núcleo), mais moldura nas faixas 4 e 5 e faíscas na 5.
 * As luzes (pulse, orbit, beam, hue) são automáticas por faixa; `luz` só
 * existe para o catálogo de dev forçar uma.
 *
 * Todas as medidas escalam por `k = tamanho / 112`, como no protótipo: os
 * insets de 6, 12, 16 e 7px são proporcionais, senão o emblema de 28 fica
 * com o núcleo maior que a moldura.
 *
 * Geometria inline aqui é obrigatória: inset, tamanho e proporção são
 * calculados a partir da prop `tamanho`, não são escolha de estilo.
 */

import React from 'react';
import { faixaDoNivel, coresDaFaixa } from './faixas';
import cx from '../../cx';
import s from './LevelBadge.module.scss';

/** Caminho hexagonal que os pontos da órbita percorrem. */
const CAMINHO_HEX = 'M50 0 100 25v50L50 100 0 75V25Z';

export default function LevelBadge({
  nivel = 1,
  tamanho = 112,
  luz,
  corDeFundo = 'var(--lv-color-surface-1)',
  className = '',
  ...rest
}) {
  const faixa = faixaDoNivel(nivel);
  const cores = coresDaFaixa(faixa);
  const luzes = (luz && luz !== 'auto' ? luz : faixa.luz) || 'none';

  const k = tamanho / 112;
  const altura = Math.round(tamanho * 1.107); // hexágono é mais alto que largo
  const respiro = Math.round(16 * k);

  const camada = (inset, extra) => ({
    inset: typeof inset === 'number' ? `${inset}px` : inset,
    ...extra,
  });

  const temBeam = luzes.includes('beam');
  const temPulse = luzes.includes('pulse');
  const temOrbita = luzes.includes('orbit');

  return (
    <div
      className={cx(s.raiz, className)}
      style={{ width: tamanho, height: altura }}
      /* O emblema é sempre o do tema escuro: no claro o anel vazado
         (`surface-1`) ficava branco e o hexágono perdia o desenho. Os tokens
         são variáveis por [data-theme], então isto basta. */
      data-theme="dark"
      role="img"
      aria-label={`${faixa.nome} · ${faixa.titulo}`}
      {...rest}
    >
      {temBeam && (
        <>
          <span
            className={cx(s.camada, s.beam)}
            data-lv-loop
            style={camada(-respiro, {
              background: `conic-gradient(from 0deg,transparent 0 58%,${cores.brilho}22 74%,${cores.brilho} 88%,transparent 92%)`,
            })}
          />
          <span
            className={cx(s.camada, s.beam)}
            data-lv-loop
            style={camada(-Math.round(respiro * 1.6), {
              background: `conic-gradient(from 0deg,transparent 0 60%,${cores.brilho}77 88%,transparent 92%)`,
              filter: `blur(${Math.max(3, 10 * k)}px)`,
            })}
          />
        </>
      )}

      {temPulse && (
        <>
          <span className={cx(s.camada, s.pulse)} data-lv-loop style={camada(0, { background: `${cores.brilho}59` })} />
          <span className={cx(s.camada, s.pulse2)} data-lv-loop style={camada(0, { background: `${cores.brilho}40` })} />
        </>
      )}

      {temOrbita && (
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className={s.orbita}
          style={{
            inset: -respiro,
            width: `calc(100% + ${respiro * 2}px)`,
            height: `calc(100% + ${respiro * 2}px)`,
          }}
          aria-hidden="true"
        >
          <circle r="3.2" style={{ fill: cores.brilho, filter: `drop-shadow(0 0 5px ${cores.brilho})` }}>
            <animateMotion dur="3.6s" repeatCount="indefinite" path={CAMINHO_HEX} />
          </circle>
          <circle r="2" style={{ fill: cores.brilho, opacity: 0.5 }}>
            <animateMotion dur="3.6s" begin="-0.18s" repeatCount="indefinite" path={CAMINHO_HEX} />
          </circle>
          <circle r="2.6" style={{ fill: faixa.chave === 'prata' ? '#8FA2FF' : '#FBBD04' }}>
            <animateMotion dur="3.6s" begin="-1.8s" repeatCount="indefinite" path={CAMINHO_HEX} />
          </circle>
        </svg>
      )}

      {faixa.moldura && (
        <>
          <span
            className={cx(s.camada, faixa.prisma && s.hue)}
            data-lv-loop={faixa.prisma || undefined}
            style={camada(-Math.round(7 * k), { background: cores.externo, opacity: 0.9 })}
          />
          <span
            className={s.camada}
            style={camada(-Math.round(7 * k) + Math.max(1, Math.round(2 * k)), { background: corDeFundo })}
          />
        </>
      )}

      <span
        className={cx(s.camada, faixa.prisma && s.hue)}
        data-lv-loop={faixa.prisma || undefined}
        style={camada(0, { background: cores.externo })}
      />
      <span className={s.camada} style={camada(Math.max(2, Math.round(6 * k)), { background: corDeFundo })} />
      <span className={s.camada} style={camada(Math.max(3, Math.round(12 * k)), { background: cores.nucleo })} />

      {faixa.faiscas && (
        <>
          <span
            className={s.faisca}
            data-lv-loop
            style={{ left: '18%', top: '22%', width: Math.max(4, 10 * k), height: Math.max(4, 10 * k) }}
          />
          <span
            className={cx(s.faisca, s.faisca2)}
            data-lv-loop
            style={{ right: '16%', top: '34%', width: Math.max(3, 7 * k), height: Math.max(3, 7 * k) }}
          />
          <span
            className={cx(s.faisca, s.faisca3)}
            data-lv-loop
            style={{ left: '30%', bottom: '18%', width: Math.max(3, 8 * k), height: Math.max(3, 8 * k) }}
          />
        </>
      )}

      <span className={s.numero} style={{ fontSize: Math.round(tamanho * 0.43), color: faixa.texto }}>
        {nivel}
      </span>
    </div>
  );
}
